/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import ItemList from 'flarum/common/utils/ItemList';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import Button from 'flarum/common/components/Button';
import type Mithril from 'mithril';
import * as follow_tags from '@fof-follow-tags';
import Separator from './Separator';

declare module 'flarum/forum/states/DiscussionListState' {
	export default interface DiscussionListState {
		/** Follow filter added by fof/follow-tags: `none`, `tags` or `users`. */
		followTags?: string;
	}
}

export default class FollowingFilterDropdown extends Component<Record<string, never>> {
	filters!: Record<string, Mithril.Children>;

	oninit(vnode: Mithril.Vnode<Record<string, never>, this>) {
		super.oninit(vnode);

		this.filters = follow_tags.components.FollowingPageFilterDropdown.prototype.options();
	}

	view() {
		return (
			<SelectDropdown buttonClassName="Button Button--secondary" caretIcon="far fa-chevron-down">
				{this.options().toArray()}
			</SelectDropdown>
		);
	}

	options(): ItemList<Mithril.Children> {
		const options = new ItemList<Mithril.Children>();

		// We need to add a hidden button hear so that the first separator is actually rendered
		options.add('button-hidden', <Button className="Button--hidden"></Button>);
		options.add(
			'discussions-separator',
			<Separator data-label={app.translator.trans('glowingblue-orell-thalia-common.forum.index.following_discussions')} />
		);
		this.addFilterOption(options, 'none');
		this.addFilterOption(options, 'tags');

		if (app.initializers.has('ianm-follow-users')) {
			this.addFilterOption(options, 'users');
		}

		return options;
	}

	/**
	 * Add an options to the options item list by getting it's label from the original
	 * filter options (saved in `this.filters`)
	 */
	addFilterOption(options: ItemList<Mithril.Children>, key: string) {
		const active = app.discussions.followTags === key;
		options.add(
			key,
			<Button
				onclick={() => {
					app.discussions.followTags = key;

					app.discussions.refresh();
				}}
				className={`Button Button--flat ${active ? 'active' : ''}`}
				active={active}
			>
				{this.filters[key]}
			</Button>
		);
	}
}
